import { Store } from '@ngrx/store';
import { IMAGE_ORDERING_TRACING_ACTIONS } from './image-ordering-tracing.actions';
import { Injectable } from '@angular/core';
import { genericTracingTransformer } from '@shared/tracking/tracing/generic-tracing-transformer';
import { PanAppState, Tracking } from '@panamax/app-state';
import { take, map } from 'rxjs/operators';
import { ImageOrderingTracingMessages } from './image-ordering-tracing.constants';
import { ImageOrderingError } from './image-ordering-error';

/**
 * Service responsible for handling tracking and tracing for Image Ordering flows
 */
@Injectable({
  providedIn: 'root',
})
export class ImageOrderingTracingService {
  constructor(
    private store: Store,
    private panAppState: PanAppState,
  ) {}

  /**
   * Tracks the start of image ordering upload
   */
  trackImageOrderingUpload(): void {
    this.dispatchTracingAction(
      true, // isStartOfTrace
      false, // isEndOfTrace
      {}, // additionalAttributes
      IMAGE_ORDERING_TRACING_ACTIONS.ImageOrderingSubmit,
    );
  }

  /**
   * Tracks successful completion of image ordering upload
   */
  trackImageOrderingUploadSuccess(): void {
    this.dispatchTracingAction(
      false, // isStartOfTrace
      true, // isEndOfTrace
      {}, // additionalAttributes
      IMAGE_ORDERING_TRACING_ACTIONS.ImageOrderingSubmitSuccess,
    );
  }

  /**
   * Tracks failure of image ordering upload
   * @param error The error that occurred during upload
   */
  trackImageOrderingUploadFail(error: ImageOrderingError): void {
    this.dispatchTracingAction(
      false, // isStartOfTrace
      true, // isEndOfTrace
      { errorDetails: error }, // additionalAttributes
      IMAGE_ORDERING_TRACING_ACTIONS.ImageOrderingSubmitFail,
    );
  }

  /**
   * Creates and dispatches a tracing action with common structure
   */
  private dispatchTracingAction(
    isStartOfTrace: boolean,
    isEndOfTrace: boolean,
    additionalAttributes: Record<string, unknown>,
    actionCreator: any,
  ): void {
    this.panAppState.user$
      .pipe(
        take(1),
        map(user => {
          const userId = user?.userId;

          const trackingData: Tracking = {
            tracing: {
              data: {
                traceContext: ImageOrderingTracingMessages.capability,
                isStartOfTrace,
                isEndOfTrace,
                attributes: {
                  user: {
                    id: userId,
                  },
                  event: ImageOrderingTracingMessages.event,
                  ...additionalAttributes,
                },
              },
              transformFunc: genericTracingTransformer,
            },
          };

          this.store.dispatch(actionCreator({ tracking: trackingData }));
        }),
      )
      .subscribe();
  }
}
